'use client';

import { useQuery } from '@tanstack/react-query';
import type { BaseArtifactDto } from '@terra/shared-contracts-artifact';
import { artifactClient } from '@terra/shared-rest-clients-artifact';

export const BROWSER_TOOL_CALL_ARTIFACTS_QUERY_KEY =
  'query.browser-tool-call-artifacts';

const ARTIFACT_STALE_TIME_MS = 10 * 60 * 1000;
const WATCH_ATTEMPTS = 6;
const WATCH_INTERVAL_MS = 2_000;

type ArtifactPicker = (artifacts: BaseArtifactDto[]) => BaseArtifactDto | null;

type UseBrowserToolCallArtifactParams = {
  toolCallId: string;
  enabled: boolean;
  pick: ArtifactPicker;
  watch?: boolean;
};

const fetchBrowserToolCallArtifacts = async (
  toolCallId: string,
): Promise<BaseArtifactDto[]> => {
  const response = await artifactClient.getArtifactsByFilter({
    query: { toolCallId, type: 'BROWSER' },
  });

  if (response.status !== 200) {
    throw new Error('Failed to load the browser artifacts of a tool call');
  }

  return response.body;
};

export const useBrowserToolCallArtifact = ({
  toolCallId,
  enabled,
  pick,
  watch = false,
}: UseBrowserToolCallArtifactParams): BaseArtifactDto | null => {
  const { data } = useQuery({
    queryKey: [BROWSER_TOOL_CALL_ARTIFACTS_QUERY_KEY, toolCallId],
    queryFn: () => fetchBrowserToolCallArtifacts(toolCallId),
    enabled: enabled && toolCallId.length > 0,
    staleTime: ARTIFACT_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    select: pick,
    refetchInterval: watch
      ? (query) => {
          if (query.state.data && pick(query.state.data)) {
            return false;
          }

          const attempts =
            query.state.dataUpdateCount + query.state.errorUpdateCount;

          return attempts >= WATCH_ATTEMPTS ? false : WATCH_INTERVAL_MS;
        }
      : false,
  });

  return data ?? null;
};
